import { HttpStatus } from "../constants/httpStatus";

export type ApiValidationFieldError = {
  field: string;
  message: string;
};

export type ApiEnvelope<T = unknown> = {
  success?: boolean;
  message?: string;
  data?: T;
  err?: string | { message?: string; status?: number };
  error?: {
    code?: number;
    type?: string;
    message?: string;
    field?: string;
    errors?: ApiValidationFieldError[];
  };
  statusCode?: number;
  status_code?: number;
};

export type ParsedApiError = {
  message: string;
  statusCode?: number;
  type?: string;
  field?: string;
  fieldErrors: Record<string, string>;
  errors?: ApiValidationFieldError[];
};

export function getApiErrorMessage(res: ApiEnvelope): string {
  if (typeof res.err === "string") return res.err;
  if (res.err && typeof res.err === "object" && res.err.message) return res.err.message;
  if (res.error?.message) return res.error.message;
  if (res.message) return res.message;
  return "Something went wrong!";
}

export function parseApiEnvelopeError(res: ApiEnvelope, httpStatus?: number): ParsedApiError {
  const fieldErrors: Record<string, string> = {};
  if (res.error?.field && res.error.message) {
    fieldErrors[res.error.field] = res.error.message;
  }
  if (Array.isArray(res.error?.errors)) {
    for (const item of res.error.errors) {
      if (item?.field) {
        fieldErrors[item.field] = item.message;
      }
    }
  }

  return {
    message: getApiErrorMessage(res),
    statusCode: res.status_code ?? res.statusCode ?? httpStatus,
    type: res.error?.type,
    field: res.error?.field,
    errors: res.error?.errors,
    fieldErrors,
  };
}

export function isAuthError(status: number, message: string): boolean {
  const normalized = message.toLowerCase();
  return (
    status === HttpStatus.UNAUTHORIZED ||
    normalized.includes("invalid token") ||
    normalized.includes("jwt") ||
    normalized.includes("unauthorized") ||
    normalized.includes("deactivated")
  );
}

export class ApiClientError extends Error {
  statusCode?: number;
  type?: string;
  field?: string;
  fieldErrors: Record<string, string>;
  errors?: ApiValidationFieldError[];
  response?: unknown;

  constructor(parsed: ParsedApiError, response?: unknown) {
    super(parsed.message);
    this.name = "ApiClientError";
    this.statusCode = parsed.statusCode;
    this.type = parsed.type;
    this.field = parsed.field;
    this.fieldErrors = parsed.fieldErrors;
    this.errors = parsed.errors;
    this.response = response;
  }
}

export function parseApiResponse<T>(
  response: { status: number; data: ApiEnvelope<T> }
): T {
  const res = response.data;

  if (res.err) {
    throw new ApiClientError(parseApiEnvelopeError(res, response.status), response);
  }

  if (
    res.success === false ||
    res.error?.type ||
    (response.status >= HttpStatus.BAD_REQUEST && res.success !== true)
  ) {
    throw new ApiClientError(parseApiEnvelopeError(res, response.status), response);
  }

  return res.data as T;
}
